import { useEffect, useMemo, useRef, useState } from 'react';
import { api, isAuthPending } from '../lib/api';
import type { ExtractedEvent, Venue } from '../types';
import { formatRange, isAllDayEvent } from '../lib/eventDates';

interface Props {
  events: ExtractedEvent[]; // 2+ events the admin selected
  venues: Venue[];
  onClose: () => void;
  onMerged: (winnerId: string, loserIds: string[]) => void;
}

interface FieldChoice<T> {
  value: T;
  fromIndex: number; // index into events[]
}

/**
 * Pick the "best" candidate for each field across the selected events. The
 * heuristic is intentionally simple — admins always have the option to
 * override the winner choice and the per-field source after preview.
 */
function pickBest(events: ExtractedEvent[]): {
  winnerIndex: number;
  patch: Partial<ExtractedEvent>;
} {
  // Winner = the event with the most populated rich-metadata fields. Ties
  // broken by highest confidence, then earliest creation. This is what the
  // admin will most likely want to keep around because it has the most useful
  // canonical info (image, ticket, performers, etc.).
  const score = (e: ExtractedEvent) =>
    Number(!!e.imageUrl) + Number(!!e.ticketUrl) + Number(!!e.eventType) +
    Number(!!e.priceInfo) + Number((e.tags?.length ?? 0) > 0) +
    Number((e.performers?.length ?? 0) > 0) + Number(!!e.organizer) +
    Number(!!e.description) + Number(!!e.venueId);
  let winnerIndex = 0;
  for (let i = 1; i < events.length; i++) {
    const a = score(events[winnerIndex]);
    const b = score(events[i]);
    if (b > a || (b === a && events[i].confidence > events[winnerIndex].confidence)) {
      winnerIndex = i;
    }
  }

  const longest = (key: keyof ExtractedEvent): FieldChoice<string> | undefined => {
    let best: FieldChoice<string> | undefined;
    events.forEach((e, idx) => {
      const v = e[key];
      if (typeof v === 'string' && v.trim().length > 0) {
        if (!best || v.length > best.value.length) best = { value: v, fromIndex: idx };
      }
    });
    return best;
  };
  const firstTruthy = <T,>(key: keyof ExtractedEvent): FieldChoice<T> | undefined => {
    let best: FieldChoice<T> | undefined;
    events.forEach((e, idx) => {
      const v = e[key] as T | undefined;
      if (!best && v !== undefined && v !== null && v !== '') best = { value: v as T, fromIndex: idx };
    });
    return best;
  };
  const unionStrings = (key: 'tags' | 'performers'): string[] | undefined => {
    const set = new Set<string>();
    for (const e of events) for (const v of e[key] ?? []) set.add(v);
    return set.size > 0 ? [...set] : undefined;
  };

  const patch: Partial<ExtractedEvent> = {};
  // String fields — keep the longest (assumed most descriptive)
  const longTitle = longest('title');
  const longDesc = longest('description');
  const longSummary = longest('aiSummary');
  const longLoc = longest('locationText');
  const longOrg = longest('organizer');
  const longPrice = longest('priceInfo');
  if (longTitle) patch.title = longTitle.value;
  if (longDesc) patch.description = longDesc.value;
  if (longSummary) patch.aiSummary = longSummary.value;
  if (longLoc) patch.locationText = longLoc.value;
  if (longOrg) patch.organizer = longOrg.value;
  if (longPrice) patch.priceInfo = longPrice.value;
  // Single-value fields — first non-empty wins
  const venue = firstTruthy<string>('venueId');
  if (venue) {
    patch.venueId = venue.value;
    patch.venueName = events[venue.fromIndex].venueName;
  }
  const ticket = firstTruthy<string>('ticketUrl');
  if (ticket) patch.ticketUrl = ticket.value;
  const image = firstTruthy<string>('imageUrl');
  if (image) patch.imageUrl = image.value;
  const evType = firstTruthy<string>('eventType');
  if (evType) patch.eventType = evType.value;
  const age = firstTruthy<string>('ageRestriction');
  if (age) patch.ageRestriction = age.value;
  const recur = firstTruthy<string>('recurringPattern');
  if (recur) patch.recurringPattern = recur.value;
  // Earliest start, latest end
  const sorted = [...events].sort((a, b) => a.startUtc.localeCompare(b.startUtc));
  patch.startUtc = sorted[0].startUtc;
  const ends = events.map((e) => e.endUtc).filter((v): v is string => !!v).sort();
  if (ends.length > 0) patch.endUtc = ends[ends.length - 1];
  // Union arrays
  const tags = unionStrings('tags');
  if (tags) patch.tags = tags;
  const performers = unionStrings('performers');
  if (performers) patch.performers = performers;
  // Boolean — true wins
  if (events.some((e) => e.isFreeEntry)) patch.isFreeEntry = true;

  return { winnerIndex, patch };
}

function fmt(iso?: string, isAllDay = false): string {
  if (!iso) return '—';
  return formatRange({ startUtc: iso, isAllDay }, {
    date: { weekday: undefined, month: 'short', day: 'numeric', year: 'numeric' },
  });
}

export default function MergeEventsModal({ events, venues, onClose, onMerged }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const venueById = useMemo(() => new Map(venues.map((v) => [v.rowKey, v])), [venues]);

  const initial = useMemo(() => pickBest(events), [events]);
  const [winnerIndex, setWinnerIndex] = useState(initial.winnerIndex);
  const [merged, setMerged] = useState<Partial<ExtractedEvent>>(initial.patch);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (events.length < 2) return;
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [events.length, onClose]);

  if (events.length < 2) return null;

  const winner = events[winnerIndex];
  const losers = events.filter((_, i) => i !== winnerIndex);

  const setField = <K extends keyof ExtractedEvent>(key: K, value: ExtractedEvent[K] | undefined) => {
    setMerged((m) => ({ ...m, [key]: value }));
  };

  // For each field, allow the admin to pick the source event's value with a
  // single click. This is the cherry-pick UI that turns merge into a real
  // training signal: the winner ends up with whatever the admin says is right.
  const FieldRow = ({
    label,
    field,
    render,
  }: {
    label: string;
    field: keyof ExtractedEvent;
    render?: (v: unknown, event: ExtractedEvent) => string;
  }) => (
    <div className="row g-2 align-items-start border-top py-2">
      <div className="col-md-3 small fw-semibold pt-1">{label}</div>
      <div className="col-md-9">
        <div className="d-flex flex-wrap gap-2 mb-1">
          {events.map((e, idx) => {
            const v = e[field];
            const display = render ? render(v, e) : (v == null || v === '' ? '—' : Array.isArray(v) ? v.join(', ') : String(v));
            const isCurrent = JSON.stringify(merged[field] ?? null) === JSON.stringify(v ?? null);
            return (
              <button
                key={idx}
                type="button"
                className={`btn btn-sm ${isCurrent ? 'btn-primary' : 'btn-outline-secondary'} text-start`}
                onClick={() => setField(field, v as never)}
                style={{ maxWidth: '100%' }}
                title={`Use value from event #${idx + 1}`}
              >
                <span className="badge text-bg-light border me-1">#{idx + 1}</span>
                <span className="text-truncate d-inline-block align-middle" style={{ maxWidth: '24rem' }}>
                  {display}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const handleMerge = async () => {
    setSaving(true);
    setError(null);
    try {
      // Convert UI patch to backend payload (JSON-encode array fields).
      const winnerPatch: Record<string, unknown> = { ...merged };
      if (Array.isArray(merged.tags)) {
        winnerPatch.tagsJson = merged.tags.length ? JSON.stringify(merged.tags) : undefined;
        delete winnerPatch.tags;
      }
      if (Array.isArray(merged.performers)) {
        winnerPatch.performersJson = merged.performers.length ? JSON.stringify(merged.performers) : undefined;
        delete winnerPatch.performers;
      }
      // Strip read-only fields the backend doesn't want.
      delete winnerPatch.partitionKey;
      delete winnerPatch.rowKey;
      delete winnerPatch.createdAt;
      delete winnerPatch.updatedAt;

      await api.post('/api/mgmt/events/merge', {
        winner: { partitionKey: winner.partitionKey, rowKey: winner.rowKey },
        losers: losers.map((l) => ({ partitionKey: l.partitionKey, rowKey: l.rowKey })),
        winnerPatch
      });
      onMerged(winner.rowKey, losers.map((l) => l.rowKey));
      onClose();
    } catch (e) {
      if (!isAuthPending(e)) setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mh-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="merge-events-title"
      onClick={onClose}
    >
      <div className="mh-modal-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '64rem' }}>
        <header className="d-flex align-items-start gap-2 p-3 border-bottom">
          <h2 id="merge-events-title" className="h5 mb-0 flex-grow-1">
            Merge {events.length} events into one
          </h2>
          <button ref={closeRef} type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </header>

        <div className="p-3">
          {error && <div className="alert alert-danger" role="alert">{error}</div>}

          <p className="small text-muted mb-3">
            Pick which event becomes the canonical "winner". The others will be marked
            <code className="mx-1">duplicate</code> with <code>duplicateOf</code> pointing at the winner.
            Each merge is recorded as a training signal so the AI learns which raw inputs
            describe the same event.
          </p>

          {/* Winner picker */}
          <fieldset className="mb-3">
            <legend className="form-label small fw-semibold">Winner</legend>
            <div className="d-flex flex-wrap gap-2">
              {events.map((e, idx) => (
                <label
                  key={e.rowKey}
                  className={`btn btn-sm ${idx === winnerIndex ? 'btn-success' : 'btn-outline-secondary'}`}
                >
                  <input
                    type="radio"
                    className="visually-hidden"
                    checked={idx === winnerIndex}
                    onChange={() => setWinnerIndex(idx)}
                    name="merge-winner"
                  />
                  <span className="badge text-bg-light border me-1">#{idx + 1}</span>
                  {e.title}
                  <span className="text-muted ms-2">{fmt(e.startUtc, isAllDayEvent(e))}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Per-field cherry-pick */}
          <section aria-labelledby="merge-fields-heading" className="mb-3">
            <h3 id="merge-fields-heading" className="h6">Final values</h3>
            <FieldRow label="Title" field="title" />
            <FieldRow label="Start" field="startUtc" render={(v, event) => fmt(v as string, isAllDayEvent(event))} />
            <FieldRow label="End" field="endUtc" render={(v, event) => fmt(v as string | undefined, isAllDayEvent(event))} />
            <FieldRow
              label="Venue"
              field="venueId"
              render={(v) => (v ? venueById.get(v as string)?.name ?? (v as string) : '—')}
            />
            <FieldRow label="Location text" field="locationText" />
            <FieldRow label="Description" field="description" render={(v) => v ? String(v).slice(0, 120) : '—'} />
            <FieldRow label="AI summary" field="aiSummary" render={(v) => v ? String(v).slice(0, 120) : '—'} />
            <FieldRow label="Image URL" field="imageUrl" />
            <FieldRow label="Ticket URL" field="ticketUrl" />
            <FieldRow label="Event type" field="eventType" />
            <FieldRow label="Tags" field="tags" />
            <FieldRow label="Performers" field="performers" />
            <FieldRow label="Organizer" field="organizer" />
            <FieldRow label="Price info" field="priceInfo" />
            <FieldRow label="Free entry" field="isFreeEntry" render={(v) => (v ? 'yes' : 'no')} />
            <FieldRow label="Age restriction" field="ageRestriction" />
            <FieldRow label="Recurring" field="recurringPattern" />
          </section>
        </div>

        <footer className="d-flex justify-content-end gap-2 p-3 border-top">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void handleMerge()} disabled={saving}>
            {saving ? 'Merging…' : `Merge — keep #${winnerIndex + 1}, mark ${losers.length} as duplicate`}
          </button>
        </footer>
      </div>
    </div>
  );
}
